import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@/components/link';
import { SvgBag, SvgBookmark, SvgHandCoin, SvgSetting, SvgShine, SvgViewThumbnail } from '@/components/icons-eataly';
import { cn } from '@/lib/utils';
import AccountLoyaltyLevelProgressBar from 'src/components/account-loyalty/account-loyalty-level-progressbar';
import { useLoyalty, useLoyaltyLoading } from '@/providers/loyalty';

export interface EatalyProfileDropdownPanelProps {
    onNavigate?: () => void;
}

const menuItemClassName = cn(
    'flex w-full items-center gap-2.5 rounded-sm py-1 text-sm font-bold tracking-[0.01em] text-[var(--neutral-10)]',
    'outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--brown-30)] focus-visible:ring-offset-2'
);

export function EatalyProfileDropdownPanel({ onNavigate }: EatalyProfileDropdownPanelProps): ReactElement {
    const { t } = useTranslation('header', { keyPrefix: 'profileMenu' });
    const loyalty = useLoyalty();
    const isLoyaltyLoading = useLoyaltyLoading();
    const showLoyaltySection = isLoyaltyLoading || Boolean(loyalty?.loyalty);
    const showLoyaltyNavItems = Boolean(loyalty?.loyalty);
    const items = useMemo(() => {
        const baseItems = [
            {
                to: '/account',
                label: t('myAccount'),
                icon: <SvgViewThumbnail className="size-6 shrink-0" aria-hidden />,
            },
            {
                to: '/account/orders',
                label: t('orders'),
                icon: <SvgBag className="size-6 shrink-0" aria-hidden />,
            },
            {
                to: '/account/wishlist',
                label: t('myLists'),
                icon: <SvgBookmark className="size-6 shrink-0" aria-hidden />,
            },
            {
                to: '/account/profile',
                label: t('accountSettings'),
                icon: <SvgSetting className="size-6 shrink-0" aria-hidden />,
            },
        ] as const;

        if (!showLoyaltyNavItems) {
            return baseItems;
        }

        const loyaltyItems = [
            {
                to: '/account/benefits',
                label: t('benefits'),
                icon: <SvgShine className="size-6 shrink-0" aria-hidden />,
            },
            {
                to: '/account/missions',
                label: t('missions'),
                icon: <SvgHandCoin className="size-6 shrink-0" aria-hidden />,
            },
        ] as const;

        return [...baseItems.slice(0, 3), ...loyaltyItems, baseItems[3]];
    }, [showLoyaltyNavItems, t]);

    return (
        <div className="flex w-full min-w-0 flex-col gap-4 px-6 py-4" data-testid="eataly-profile-dropdown-panel">
            {showLoyaltySection ? (
                <>
                    <AccountLoyaltyLevelProgressBar compact />
                    <div
                        className="h-px w-full bg-[var(--sand-90)]"
                        aria-hidden
                        data-testid="eataly-profile-loyalty-divider"
                    />
                </>
            ) : null}
            <nav aria-label={t('navAriaLabel')} className="flex flex-col gap-2">
                {items.map(({ to, label, icon }) => (
                    <Link key={label} to={to} className={menuItemClassName} onClick={() => onNavigate?.()}>
                        {icon}
                        {label}
                    </Link>
                ))}
            </nav>
        </div>
    );
}
